import { Children, useCallback, useLayoutEffect, useRef, useState, type MouseEvent, type ReactElement, type TouchEvent } from 'react'
import * as S from './swiper.styled'
import { type SwiperProps } from './swiper.props'
import arrow from './assets/arrow.svg'

export function Swiper ({ children, className = '', fade = false, offset = 0, showArrows = false }: SwiperProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [didMove, setDidMove] = useState(false)
  const [effectiveOffset, setEffectiveOffset] = useState(offset)
  const [isDragging, setIsDragging] = useState(false)
  const [initialTranslateX, setInitialTranslateX] = useState(0)
  const [translateX, setTranslateX] = useState(offset)
  const [startX, setStartX] = useState(0)
  const totalSlides = Children.count(children)

  useLayoutEffect(() => {
    const container = containerRef.current
    const scroller = scrollerRef.current
    if (!container || !scroller) return

    const updateLayout = (): void => {
      const containerWidth = container.offsetWidth
      const scrollerWidth = scroller.scrollWidth

      if (containerWidth >= scrollerWidth) {
        setEffectiveOffset(0)
        setTranslateX(0)
        container.style.cursor = 'default'
        scroller.style.transform = 'translateX(0px)'
        scroller.style.justifyContent = 'center'
      } else {
        setEffectiveOffset(offset)
        setTranslateX(offset)
        container.style.cursor = 'grab'
        scroller.style.transform = `translateX(${offset}px)`
        scroller.style.justifyContent = 'initial'
      }
    }

    updateLayout()

    let timeoutId: ReturnType<typeof setTimeout>

    const handleResize = (): void => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateLayout, 100)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, [offset])

  const startDrag = useCallback((clientX: number) => {
    setIsDragging(true)
    setStartX(clientX)
    setInitialTranslateX(translateX)
  }, [translateX])

  const handleMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
    startDrag(e.clientX)
  }, [startDrag])

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false)
    setTimeout(() => { setDidMove(false) }, 0)
  }, [])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setTimeout(() => { setDidMove(false) }, 0)
  }, [])

  const handleMouseMove = useCallback((evt: MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollerRef.current || !containerRef.current) return
    evt.preventDefault()

    const distance = evt.clientX - startX
    if (Math.abs(distance) > 5) setDidMove(true)
    let newTranslateX = initialTranslateX + distance
    const containerWidth = containerRef.current.offsetWidth
    const cardsWidth = scrollerRef.current.scrollWidth
    const minTranslateX = containerWidth - cardsWidth - effectiveOffset
    newTranslateX = Math.max(minTranslateX, Math.min(effectiveOffset, newTranslateX))
    setTranslateX(newTranslateX)
    scrollerRef.current.style.transform = `translateX(${newTranslateX}px)`
  }, [initialTranslateX, isDragging, effectiveOffset, startX])

  const handleTouchStart = useCallback((evt: TouchEvent<HTMLDivElement>) => {
    const touch = evt.touches[0]
    startDrag(touch.clientX)
  }, [startDrag])

  const handleTouchMove = useCallback((evt: TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollerRef.current || !containerRef.current) return
    evt.preventDefault()

    const touch = evt.touches[0]
    const distance = touch.clientX - startX
    if (Math.abs(distance) > 5) setDidMove(true)
    let newTranslateX = initialTranslateX + distance
    const containerWidth = containerRef.current.offsetWidth
    const cardsWidth = scrollerRef.current.scrollWidth
    const minTranslateX = containerWidth - cardsWidth - effectiveOffset
    newTranslateX = Math.max(minTranslateX, Math.min(effectiveOffset, newTranslateX))
    setTranslateX(newTranslateX)
    scrollerRef.current.style.transform = `translateX(${newTranslateX}px)`
  }, [initialTranslateX, isDragging, effectiveOffset, startX])

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleNext = useCallback(() => {
    if (!scrollerRef.current || !containerRef.current) return
    const firstChild = scrollerRef.current.children[0] as HTMLElement
    if (!firstChild) return
    const slideWidth = firstChild.offsetWidth
    const maxTranslateX = containerRef.current.offsetWidth - scrollerRef.current.scrollWidth + effectiveOffset
    const newTranslateX = Math.max(maxTranslateX, translateX - slideWidth)
    setTranslateX(newTranslateX)
    scrollerRef.current.style.transform = `translateX(${newTranslateX}px)`
  }, [effectiveOffset, translateX])

  const handlePrevious = useCallback(() => {
    if (!scrollerRef.current) return
    const firstChild = scrollerRef.current.children[0] as HTMLElement
    if (!firstChild) return
    const slideWidth = firstChild.offsetWidth
    const newTranslateX = Math.min(effectiveOffset, translateX + slideWidth)
    setTranslateX(newTranslateX)
    scrollerRef.current.style.transform = `translateX(${newTranslateX}px)`
  }, [effectiveOffset, translateX])

  return <S.Container className={className}>
    <div
      className="container"
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      ref={containerRef}
    >
      <div className={`cards ${didMove ? 'click-blocked' : ''}`} ref={scrollerRef}>
        {children}
      </div>
    </div>
    {fade && <S.FadeOverlay />}
    {(showArrows && totalSlides > 1) && <button className="arrow previous" onClick={handlePrevious}>
      <img src={arrow} alt="Slide anterior" />
    </button>}
    {(showArrows && totalSlides > 1) && <button className="arrow next" onClick={handleNext}>
      <img src={arrow} alt="Próximo slide" />
    </button>}
  </S.Container>
}
